// geographycontent10.js
// Section Ten — A Study of Two Contrasting Regions
// Grade9Ready Geography content module
//
// Covers:
// 132 Nigeria — Physical Geography
// 133 Nigeria — Population
// 134 Nigeria — Farming and Industry
// 135 Nigeria — Lagos
// 136 Japan — Physical Geography
// 137 Japan — Population
// 138 Japan — Farming and Industry
// 139 Japan — Osaka
// 140 Comparison of Nigeria and Japan
// 144 Revision Summary for Section Ten

export const geographyContent10 = {
  id: "geography-section-10",
  subject: "Geography",
  sectionNumber: 10,
  title: "A Study of Two Contrasting Regions",
  slug: "two-contrasting-regions",
  examBoardLink:
    "KS3 Geography: place knowledge, regional comparison, human and physical geography, development, population and economic activity",
  intro:
    "This section compares two contrasting regions: Nigeria and Japan. It looks at their physical geography, population, farming, industry and major cities. The aim is to understand how different physical environments, levels of development, population structures and economic activities create very different opportunities and challenges.",
  keySkills: [
    "Describe the physical and human geography of contrasting countries.",
    "Compare population patterns and population structures.",
    "Explain how physical geography influences farming, settlement and industry.",
    "Use place-specific detail to support explanations.",
    "Compare levels of development using evidence.",
    "Explain similarities and differences between Lagos and Osaka.",
    "Write balanced comparison answers using both countries."
  ],

  topics: [
    {
      id: "geo10-1-nigeria-physical-geography",
      title: "Nigeria — Physical Geography",
      slug: "nigeria-physical-geography",
      pageRef: "Source booklet p.132",
      learningObjectives: [
        "Locate Nigeria within Africa.",
        "Describe Nigeria’s main physical features.",
        "Explain how climate and relief influence life in Nigeria."
      ],
      keywords: [
        {
          term: "Nigeria",
          definition:
            "A country in West Africa with a large population, varied landscapes and a rapidly growing economy."
        },
        {
          term: "West Africa",
          definition:
            "The western region of the African continent, bordering the Atlantic Ocean."
        },
        {
          term: "Relief",
          definition:
            "The height and shape of the land."
        },
        {
          term: "Climate",
          definition:
            "The long-term pattern of weather in a place."
        },
        {
          term: "Savanna",
          definition:
            "Tropical grassland with scattered trees, usually with wet and dry seasons."
        },
        {
          term: "Tropical rainforest",
          definition:
            "A hot, wet forest ecosystem found near the Equator."
        },
        {
          term: "River Niger",
          definition:
            "A major river in West Africa that flows through Nigeria."
        },
        {
          term: "Niger Delta",
          definition:
            "A low-lying wetland area where the River Niger reaches the Atlantic Ocean."
        }
      ],
      tutorial: [
        {
          heading: "Where Nigeria is",
          body:
            "Nigeria is located in West Africa on the Gulf of Guinea. It has a coastline on the Atlantic Ocean and shares borders with countries such as Benin, Niger, Chad and Cameroon."
        },
        {
          heading: "Physical landscape",
          body:
            "Nigeria has varied physical geography, including coastal lowlands, the Niger Delta, river valleys, savanna, rainforest and upland areas. The River Niger and River Benue are important physical features."
        },
        {
          heading: "Climate and vegetation",
          body:
            "Nigeria’s climate varies from wetter tropical areas in the south to drier savanna and semi-arid conditions further north. This affects farming, settlement, vegetation and water supply."
        },
        {
          heading: "Why physical geography matters",
          body:
            "Physical geography influences where people live, what crops can be grown, how people travel, and where industries develop. For example, rivers and the coast can support trade, transport and fishing, while drier northern areas may face greater water challenges."
        }
      ],
      workedExamples: [
        {
          question:
            "Explain how Nigeria’s climate affects farming.",
          answer:
            "Southern Nigeria is wetter and can support crops that need more rainfall, such as cocoa and oil palm. Northern Nigeria is drier, so farming may focus more on crops and livestock suited to savanna conditions."
        },
        {
          question:
            "Why is the Niger Delta important?",
          answer:
            "The Niger Delta is important because it contains wetlands, fishing resources and major oil reserves. However, it also faces environmental problems linked to oil extraction and pollution."
        }
      ],
      commonMisconceptions: [
        "Nigeria does not have one single landscape or climate; it varies from wetter south to drier north.",
        "Nigeria is not just rural; it also has major cities and industrial areas.",
        "Physical geography can create opportunities and challenges at the same time."
      ],
      quickCheck: [
        {
          question:
            "Which major river flows through Nigeria?",
          options: ["River Niger", "River Thames", "River Seine", "River Volga"],
          answer: "River Niger",
          explanation:
            "The River Niger is one of Nigeria’s most important physical features."
        },
        {
          question:
            "Which area of Nigeria is generally wetter?",
          options: ["The south", "The far north", "The Sahara interior", "All areas equally"],
          answer: "The south",
          explanation:
            "Southern Nigeria has wetter tropical conditions than the drier north."
        }
      ],
      examPractice: [
        {
          question:
            "Describe Nigeria’s physical geography.",
          marks: 4,
          indicativeContent:
            "Nigeria is in West Africa and has a coastline on the Gulf of Guinea. It includes coastal lowlands, the Niger Delta, river valleys, savanna, rainforest and upland areas. The south is generally wetter, while the north is drier."
        },
        {
          question:
            "Explain how Nigeria’s physical geography affects people and economic activity.",
          marks: 6,
          indicativeContent:
            "Nigeria’s rivers and coastline support trade, fishing, transport and settlement. The Niger Delta contains oil reserves, creating income and industry, but also pollution risks. Wetter southern areas support crops such as cocoa and oil palm, while drier northern savanna areas are more suited to livestock and drought-resistant crops. Physical geography therefore shapes farming, industry and settlement."
        }
      ],
      grade9Move:
        "Use north-south contrast in Nigeria: wetter south, drier north, coastal trade and Niger Delta oil. This adds place-specific detail."
    },

    {
      id: "geo10-2-nigeria-population",
      title: "Nigeria — Population",
      slug: "nigeria-population",
      pageRef: "Source booklet p.133",
      learningObjectives: [
        "Describe Nigeria’s population pattern.",
        "Explain why Nigeria’s population is growing.",
        "Identify opportunities and challenges linked to population growth."
      ],
      keywords: [
        {
          term: "Population growth",
          definition:
            "An increase in the number of people living in a place."
        },
        {
          term: "Natural increase",
          definition:
            "Population growth caused when birth rate is higher than death rate."
        },
        {
          term: "Birth rate",
          definition:
            "The number of live births per 1,000 people per year."
        },
        {
          term: "Death rate",
          definition:
            "The number of deaths per 1,000 people per year."
        },
        {
          term: "Youthful population",
          definition:
            "A population with a large proportion of children and young people."
        },
        {
          term: "Urbanisation",
          definition:
            "An increase in the proportion of people living in towns and cities."
        },
        {
          term: "Population density",
          definition:
            "The average number of people living per square kilometre."
        },
        {
          term: "Migration",
          definition:
            "Movement of people from one place to another to live."
        }
      ],
      tutorial: [
        {
          heading: "Nigeria’s population",
          body:
            "Nigeria is one of Africa’s most populous countries. Its population is large and growing, with many young people. This creates a large future workforce but also pressure on schools, healthcare, housing and jobs."
        },
        {
          heading: "Why population is growing",
          body:
            "Population growth is linked to high birth rates, falling death rates, improved healthcare and a youthful population. When death rates fall but birth rates remain relatively high, natural increase is rapid."
        },
        {
          heading: "Population distribution",
          body:
            "Nigeria’s population is unevenly distributed. Some areas, especially major cities and more economically active regions, are densely populated. Other areas are less densely populated because of climate, relief, services or economic opportunities."
        },
        {
          heading: "Urbanisation",
          body:
            "Many people move from rural areas to cities for work, education, healthcare and services. This increases urban growth, especially in major cities such as Lagos."
        }
      ],
      workedExamples: [
        {
          question:
            "Explain why Nigeria has a youthful population.",
          answer:
            "Nigeria has had high birth rates and a falling death rate. This means many children are born and more survive into adulthood, creating a population with a large proportion of young people."
        },
        {
          question:
            "Explain one challenge of rapid population growth in Nigeria.",
          answer:
            "Rapid population growth increases demand for schools, healthcare, housing, water and jobs. If services cannot keep up, overcrowding and unemployment may increase."
        }
      ],
      commonMisconceptions: [
        "A large population is not automatically negative; it can provide a large workforce and market.",
        "Population growth is affected by birth rates, death rates and migration.",
        "Youthful populations create opportunities if education and jobs are available."
      ],
      quickCheck: [
        {
          question:
            "What happens when birth rate is higher than death rate?",
          options: ["Natural increase", "Natural decrease", "Deindustrialisation", "Acid rain"],
          answer: "Natural increase",
          explanation:
            "Natural increase occurs when more people are born than die."
        },
        {
          question:
            "Which is a likely challenge of rapid population growth?",
          options: [
            "Pressure on housing and services",
            "No demand for schools",
            "No need for jobs",
            "Guaranteed lower urban growth"
          ],
          answer: "Pressure on housing and services",
          explanation:
            "More people require more housing, healthcare, education and infrastructure."
        }
      ],
      examPractice: [
        {
          question:
            "Explain why Nigeria’s population is growing rapidly.",
          marks: 6,
          indicativeContent:
            "Nigeria’s population is growing because birth rates remain relatively high while death rates have fallen due to better healthcare, vaccination, sanitation and food supply. A youthful population means many people are of child-bearing age, which can keep birth numbers high. Rural-urban migration also increases the population of major cities."
        },
        {
          question:
            "Assess whether Nigeria’s large population is an opportunity or a challenge.",
          marks: 8,
          indicativeContent:
            "Nigeria’s large population can be an opportunity because it provides a large workforce, large consumer market and potential for economic growth. However, rapid growth creates challenges, including pressure on housing, schools, healthcare, water supply and jobs. If education and employment improve, the youthful population could support development. If not, unemployment and inequality may increase."
        }
      ],
      grade9Move:
        "Balance population growth as both demographic opportunity and service pressure. This avoids one-sided answers."
    },

    {
      id: "geo10-3-nigeria-farming-and-industry",
      title: "Nigeria — Farming and Industry",
      slug: "nigeria-farming-and-industry",
      pageRef: "Source booklet p.134",
      learningObjectives: [
        "Describe farming in Nigeria.",
        "Explain Nigeria’s main industries.",
        "Understand how natural resources support economic activity."
      ],
      keywords: [
        {
          term: "Agriculture",
          definition:
            "Farming, including growing crops and raising animals."
        },
        {
          term: "Subsistence farming",
          definition:
            "Farming mainly to feed the farmer and their family."
        },
        {
          term: "Commercial farming",
          definition:
            "Farming to sell crops or animals for profit."
        },
        {
          term: "Cash crop",
          definition:
            "A crop grown mainly to be sold, such as cocoa, cotton or oil palm."
        },
        {
          term: "Oil industry",
          definition:
            "Economic activity based on extracting, refining and selling oil."
        },
        {
          term: "Manufacturing",
          definition:
            "Making products from raw materials or components."
        },
        {
          term: "Export",
          definition:
            "A good or service sold to another country."
        },
        {
          term: "Diversification",
          definition:
            "Developing a wider range of economic activities to reduce dependence on one sector."
        }
      ],
      tutorial: [
        {
          heading: "Farming in Nigeria",
          body:
            "Farming remains important in Nigeria. Different crops are grown in different regions depending on climate and soil. Wetter southern areas can support crops such as cocoa and oil palm, while northern areas may grow crops suited to drier conditions and support livestock."
        },
        {
          heading: "Oil and natural resources",
          body:
            "Nigeria has important oil reserves, especially in the Niger Delta. Oil exports bring income and government revenue, but dependence on oil can be risky if prices fall or if pollution damages local environments."
        },
        {
          heading: "Industry and cities",
          body:
            "Nigeria has manufacturing, construction, food processing, textiles, cement, services and technology growth, especially in urban areas. Cities create markets, workers and transport links."
        },
        {
          heading: "Challenges",
          body:
            "Nigeria’s economy faces challenges such as unreliable electricity, infrastructure gaps, regional inequality, environmental damage, unemployment and reliance on oil exports."
        }
      ],
      workedExamples: [
        {
          question:
            "Explain why oil is important to Nigeria’s economy.",
          answer:
            "Oil exports earn foreign income and provide government revenue. The oil industry also creates jobs and supports related industries such as refining, transport and engineering."
        },
        {
          question:
            "Explain one risk of relying heavily on oil.",
          answer:
            "Oil prices can change quickly. If prices fall, export income and government revenue may drop. Oil extraction can also damage environments through spills and pollution."
        }
      ],
      commonMisconceptions: [
        "Nigeria’s economy is not only farming; it also includes oil, manufacturing, services and technology.",
        "Natural resources can create wealth but also environmental and political challenges.",
        "Farming varies across Nigeria because climate and soils vary."
      ],
      quickCheck: [
        {
          question:
            "What is a cash crop?",
          options: [
            "A crop grown mainly for sale",
            "A crop grown only for decoration",
            "A crop that cannot be traded",
            "A type of fossil fuel"
          ],
          answer: "A crop grown mainly for sale",
          explanation:
            "Cash crops are grown to generate income."
        },
        {
          question:
            "Which Nigerian region is strongly associated with oil?",
          options: ["Niger Delta", "Mount Fuji", "Osaka Bay", "The Alps"],
          answer: "Niger Delta",
          explanation:
            "The Niger Delta is a major oil-producing region."
        }
      ],
      examPractice: [
        {
          question:
            "Explain how physical geography influences farming in Nigeria.",
          marks: 6,
          indicativeContent:
            "Nigeria’s farming varies because climate and vegetation change from south to north. The wetter south supports crops such as cocoa and oil palm, while the drier north is more suited to drought-resistant crops and livestock. Rivers and fertile soils can support farming, while dry conditions or poor infrastructure can limit production."
        },
        {
          question:
            "Evaluate the importance of oil to Nigeria’s development.",
          marks: 8,
          indicativeContent:
            "Oil is important because it earns export income, provides government revenue, creates jobs and supports related industries. This can fund infrastructure and services. However, dependence on oil is risky because prices change and wealth may not be evenly shared. Oil spills and pollution can damage farming, fishing and ecosystems in the Niger Delta. Overall, oil supports development but Nigeria benefits more if the economy diversifies."
        }
      ],
      grade9Move:
        "In Nigeria industry answers, link oil to both development and environmental inequality. That creates a more evaluative response."
    },

    {
      id: "geo10-4-nigeria-lagos",
      title: "Nigeria — Lagos",
      slug: "nigeria-lagos",
      pageRef: "Source booklet p.135",
      learningObjectives: [
        "Describe the location and importance of Lagos.",
        "Explain why Lagos has grown rapidly.",
        "Evaluate opportunities and challenges in Lagos."
      ],
      keywords: [
        {
          term: "Lagos",
          definition:
            "Nigeria’s largest city and a major economic centre in West Africa."
        },
        {
          term: "Megacity",
          definition:
            "A city with a population of more than 10 million people."
        },
        {
          term: "Urban growth",
          definition:
            "An increase in the population or physical size of an urban area."
        },
        {
          term: "Rural-urban migration",
          definition:
            "Movement of people from countryside areas to towns and cities."
        },
        {
          term: "Informal settlement",
          definition:
            "Housing built without formal planning permission, often lacking basic services."
        },
        {
          term: "Infrastructure",
          definition:
            "Basic systems and services such as roads, electricity, water, sewers and transport."
        },
        {
          term: "Congestion",
          definition:
            "Overcrowding on roads or transport networks."
        },
        {
          term: "Informal economy",
          definition:
            "Economic activity that is not officially registered, taxed or regulated."
        }
      ],
      tutorial: [
        {
          heading: "Why Lagos matters",
          body:
            "Lagos is Nigeria’s largest city and one of Africa’s major economic hubs. It has ports, industries, finance, markets, transport links, education, healthcare and cultural industries."
        },
        {
          heading: "Why Lagos has grown",
          body:
            "Lagos has grown because of rural-urban migration and natural increase. People move there for jobs, education, healthcare and better services. Its coastal location and port also support trade and industry."
        },
        {
          heading: "Opportunities",
          body:
            "Lagos offers jobs, education, healthcare, trade, entertainment and business opportunities. The city attracts investment and has a large workforce and consumer market."
        },
        {
          heading: "Challenges",
          body:
            "Rapid growth has created housing shortages, informal settlements, traffic congestion, pollution, pressure on water and sanitation, waste problems and inequality."
        }
      ],
      workedExamples: [
        {
          question:
            "Explain why Lagos has grown rapidly.",
          answer:
            "Lagos has grown because people migrate from rural areas for jobs, education and services. Natural increase also adds to the population. Its port and economic role attract businesses and workers."
        },
        {
          question:
            "Explain one challenge caused by rapid urban growth in Lagos.",
          answer:
            "Housing supply has not kept up with population growth, so informal settlements have expanded. These areas may lack clean water, sanitation, drainage and secure housing."
        }
      ],
      commonMisconceptions: [
        "Lagos is not Nigeria’s political capital, but it is a major economic centre.",
        "Informal settlements are not only a housing issue; they also link to jobs, services, health and infrastructure.",
        "Rapid urban growth creates opportunities and challenges at the same time."
      ],
      quickCheck: [
        {
          question:
            "Which word describes a city with more than 10 million people?",
          options: ["Megacity", "Hamlet", "Watershed", "Tundra"],
          answer: "Megacity",
          explanation:
            "A megacity has a population of more than 10 million."
        },
        {
          question:
            "Which is a major challenge in rapidly growing cities such as Lagos?",
          options: [
            "Housing shortages",
            "No economic activity",
            "No population growth",
            "No transport demand"
          ],
          answer: "Housing shortages",
          explanation:
            "Rapid growth can outpace formal housing supply."
        }
      ],
      examPractice: [
        {
          question:
            "Explain why Lagos is important to Nigeria.",
          marks: 6,
          indicativeContent:
            "Lagos is important because it is Nigeria’s largest city and a major economic centre. It has ports, industries, finance, markets, transport links and a large workforce. It attracts investment and provides many jobs and services. Its coastal location supports trade, making it important nationally and internationally."
        },
        {
          question:
            "Assess the opportunities and challenges created by the growth of Lagos.",
          marks: 8,
          indicativeContent:
            "Lagos provides opportunities such as jobs, education, healthcare, trade, investment and cultural activity. Its large market and port support economic growth. However, rapid growth creates housing shortages, informal settlements, congestion, pollution, waste problems and pressure on water and sanitation. Overall, Lagos is a driver of development, but growth must be managed to improve quality of life."
        }
      ],
      grade9Move:
        "For Lagos, use the phrase ‘economic opportunity versus infrastructure pressure’. This captures the core urban contrast."
    },

    {
      id: "geo10-5-japan-physical-geography",
      title: "Japan — Physical Geography",
      slug: "japan-physical-geography",
      pageRef: "Source booklet p.136",
      learningObjectives: [
        "Locate Japan within East Asia.",
        "Describe Japan’s physical geography.",
        "Explain how tectonic hazards and relief affect Japan."
      ],
      keywords: [
        {
          term: "Japan",
          definition:
            "An island country in East Asia made up of several main islands and many smaller islands."
        },
        {
          term: "Archipelago",
          definition:
            "A group or chain of islands."
        },
        {
          term: "East Asia",
          definition:
            "The eastern region of Asia, including countries such as Japan, China and Korea."
        },
        {
          term: "Tectonic plate",
          definition:
            "A large section of the Earth’s crust that moves slowly over the mantle."
        },
        {
          term: "Earthquake",
          definition:
            "Sudden shaking of the ground caused by movement in the Earth’s crust."
        },
        {
          term: "Volcano",
          definition:
            "An opening in the Earth’s crust where lava, ash and gases can erupt."
        },
        {
          term: "Tsunami",
          definition:
            "A large sea wave usually caused by an undersea earthquake or volcanic activity."
        },
        {
          term: "Mountainous relief",
          definition:
            "Land dominated by mountains and steep slopes."
        }
      ],
      tutorial: [
        {
          heading: "Where Japan is",
          body:
            "Japan is an island country in East Asia in the Pacific Ocean. It is an archipelago, with major islands including Honshu, Hokkaido, Kyushu and Shikoku."
        },
        {
          heading: "Mountainous landscape",
          body:
            "Much of Japan is mountainous, meaning flat land for farming and settlement is limited. Many people and industries are concentrated on coastal plains."
        },
        {
          heading: "Tectonic activity",
          body:
            "Japan lies near plate boundaries, so it experiences earthquakes, volcanoes and tsunami risk. This creates hazards but also influences landscapes and energy potential, such as geothermal power."
        },
        {
          heading: "Coasts and climate",
          body:
            "Japan’s long coastline supports fishing, ports and trade. Its climate varies from colder northern areas to warmer southern areas, affecting farming and settlement."
        }
      ],
      workedExamples: [
        {
          question:
            "Explain how Japan’s mountainous relief affects settlement.",
          answer:
            "Mountainous land is harder to build on and farm, so many people live on flatter coastal plains. This creates high population density in some lowland urban areas."
        },
        {
          question:
            "Why is Japan at risk from earthquakes?",
          answer:
            "Japan is located near tectonic plate boundaries. Movement of plates causes earthquakes, and undersea earthquakes can also trigger tsunamis."
        }
      ],
      commonMisconceptions: [
        "Japan is not one single island; it is an archipelago.",
        "Japan’s high population density is partly linked to limited flat land.",
        "Tectonic activity creates both risks and some opportunities, such as geothermal energy."
      ],
      quickCheck: [
        {
          question:
            "What is an archipelago?",
          options: [
            "A group or chain of islands",
            "A single river valley",
            "A desert biome",
            "A type of soil erosion"
          ],
          answer: "A group or chain of islands",
          explanation:
            "Japan is an archipelago because it is made up of many islands."
        },
        {
          question:
            "Why does Japan experience frequent earthquakes?",
          options: [
            "It is near tectonic plate boundaries",
            "It has no coastline",
            "It is completely flat",
            "It has no volcanoes"
          ],
          answer: "It is near tectonic plate boundaries",
          explanation:
            "Plate movement causes earthquakes near Japan."
        }
      ],
      examPractice: [
        {
          question:
            "Describe Japan’s physical geography.",
          marks: 4,
          indicativeContent:
            "Japan is an island country in East Asia made up of several main islands. It is mountainous with limited flat land, long coastlines and many coastal plains. It lies near tectonic plate boundaries, so it is affected by earthquakes, volcanoes and tsunami risk."
        },
        {
          question:
            "Explain how Japan’s physical geography affects people and economic activity.",
          marks: 6,
          indicativeContent:
            "Japan’s mountainous relief limits flat land for farming and settlement, so many people live on coastal plains. Its long coastline supports fishing, ports and international trade. Tectonic activity creates earthquake, volcano and tsunami hazards, requiring strong building design and emergency planning. Physical geography therefore shapes settlement, transport, farming and risk management."
        }
      ],
      grade9Move:
        "For Japan, connect limited flat land to dense coastal urban areas and strong hazard planning."
    },

    {
      id: "geo10-6-japan-population",
      title: "Japan — Population",
      slug: "japan-population",
      pageRef: "Source booklet p.137",
      learningObjectives: [
        "Describe Japan’s population distribution.",
        "Explain why Japan has an ageing population.",
        "Assess the impacts of an ageing and declining population."
      ],
      keywords: [
        {
          term: "Ageing population",
          definition:
            "A population with a growing proportion of older people."
        },
        {
          term: "Life expectancy",
          definition:
            "The average number of years a person is expected to live."
        },
        {
          term: "Fertility rate",
          definition:
            "The average number of children a woman is expected to have."
        },
        {
          term: "Population decline",
          definition:
            "A reduction in the total population of a place."
        },
        {
          term: "Dependency ratio",
          definition:
            "The relationship between working-age people and dependants such as children and older people."
        },
        {
          term: "Urban concentration",
          definition:
            "A pattern where a large proportion of people live in towns and cities."
        },
        {
          term: "Population density",
          definition:
            "The average number of people per square kilometre."
        },
        {
          term: "Pension",
          definition:
            "Money paid regularly to people after retirement."
        }
      ],
      tutorial: [
        {
          heading: "Population distribution",
          body:
            "Japan’s population is unevenly distributed. Because much of the country is mountainous, many people live in dense urban areas on coastal plains, especially around major cities."
        },
        {
          heading: "Ageing population",
          body:
            "Japan has a high life expectancy and low fertility rate. This means people live for a long time, but fewer babies are born. Over time, the proportion of older people rises."
        },
        {
          heading: "Impacts",
          body:
            "An ageing population can increase pressure on pensions, healthcare and social care. It can also reduce the working-age population, creating labour shortages and slowing economic growth."
        },
        {
          heading: "Responses",
          body:
            "Possible responses include encouraging higher birth rates, using automation and robotics, raising retirement ages, improving healthcare efficiency and allowing more immigration."
        }
      ],
      workedExamples: [
        {
          question:
            "Explain why Japan has an ageing population.",
          answer:
            "Japan has high life expectancy because healthcare, diet and living standards are strong. It also has a low fertility rate, so fewer young people are added to the population. This increases the proportion of older people."
        },
        {
          question:
            "Explain one economic challenge of Japan’s ageing population.",
          answer:
            "A smaller working-age population may mean fewer workers and lower tax revenue, while spending on pensions and healthcare increases."
        }
      ],
      commonMisconceptions: [
        "A high life expectancy is positive, but it can create planning challenges if birth rates are low.",
        "Population decline can happen even in wealthy countries.",
        "Population density in Japan is especially high on available flat land, not evenly across the whole country."
      ],
      quickCheck: [
        {
          question:
            "Which factor contributes to Japan’s ageing population?",
          options: [
            "Low fertility rate",
            "Very high birth rate",
            "No healthcare",
            "Rapid natural increase"
          ],
          answer: "Low fertility rate",
          explanation:
            "Fewer births mean fewer young people in the population."
        },
        {
          question:
            "Which service is likely to face more pressure with an ageing population?",
          options: [
            "Healthcare",
            "Primary school places only",
            "Childcare only",
            "Coal mining only"
          ],
          answer: "Healthcare",
          explanation:
            "Older populations often require more healthcare and social care."
        }
      ],
      examPractice: [
        {
          question:
            "Explain why Japan’s population is unevenly distributed.",
          marks: 6,
          indicativeContent:
            "Japan’s population is unevenly distributed because much of the country is mountainous, limiting land for farming, building and transport. Many people live on flatter coastal plains where there are jobs, ports, transport links and major cities. This creates dense urban areas and lower population density in mountainous regions."
        },
        {
          question:
            "Assess the challenges created by Japan’s ageing population.",
          marks: 8,
          indicativeContent:
            "Japan’s ageing population creates pressure on healthcare, pensions and social care because more people are living into old age. A smaller working-age population can cause labour shortages and reduce tax income. However, older people may continue working, volunteering and supporting families. Japan can also respond through automation, robotics, later retirement and carefully managed immigration. Overall, ageing is a major challenge but can be managed with planning."
        }
      ],
      grade9Move:
        "Use the phrase ‘high life expectancy plus low fertility rate’ to explain ageing population clearly."
    },

    {
      id: "geo10-7-japan-farming-and-industry",
      title: "Japan — Farming and Industry",
      slug: "japan-farming-and-industry",
      pageRef: "Source booklet p.138",
      learningObjectives: [
        "Describe farming in Japan.",
        "Explain why Japan has limited farmland.",
        "Describe Japan’s main industries and economic strengths."
      ],
      keywords: [
        {
          term: "Intensive farming",
          definition:
            "Farming that produces high output from a small area of land using labour, technology or inputs."
        },
        {
          term: "Rice cultivation",
          definition:
            "The growing of rice, often in wet paddy fields."
        },
        {
          term: "Arable farming",
          definition:
            "Growing crops."
        },
        {
          term: "Manufacturing",
          definition:
            "Making goods from raw materials or components."
        },
        {
          term: "High-tech industry",
          definition:
            "Industry based on advanced technology, research and skilled workers."
        },
        {
          term: "Export economy",
          definition:
            "An economy that earns significant income from selling goods and services abroad."
        },
        {
          term: "Automation",
          definition:
            "Using machines, robots or computer systems to carry out work."
        },
        {
          term: "Raw material imports",
          definition:
            "Natural resources bought from other countries for use in industry."
        }
      ],
      tutorial: [
        {
          heading: "Farming in Japan",
          body:
            "Japan has limited farmland because much of the country is mountainous. Farming often takes place on small areas of flat land, coastal plains or terraced slopes. Rice is an important crop."
        },
        {
          heading: "Why farming is intensive",
          body:
            "Because farmland is limited, farmers often use land carefully and intensively. Technology, irrigation, fertilisers and terracing can help increase yields from small areas."
        },
        {
          heading: "Industry in Japan",
          body:
            "Japan is highly industrialised and known for manufacturing, electronics, vehicles, robotics, precision engineering and high-tech industries. It imports many raw materials and exports high-value manufactured goods."
        },
        {
          heading: "Challenges for industry",
          body:
            "Japan faces challenges including limited natural resources, energy dependence, ageing workforce, global competition and hazard risk. However, education, technology, infrastructure and innovation help support its economy."
        }
      ],
      workedExamples: [
        {
          question:
            "Explain why Japan imports many raw materials.",
          answer:
            "Japan has limited domestic raw materials and limited land. To support manufacturing, it imports resources such as fuels, metals and other materials, then uses skilled labour and technology to make high-value products."
        },
        {
          question:
            "Explain why farming in Japan is often intensive.",
          answer:
            "Much of Japan is mountainous, so flat farmland is limited. Farmers use small areas carefully with irrigation, technology and terracing to produce high yields."
        }
      ],
      commonMisconceptions: [
        "Japan’s limited farmland does not mean farming is unimportant.",
        "Japan is resource-poor in many raw materials but economically strong because it adds value through manufacturing and technology.",
        "High-tech industry depends on skilled workers, research, infrastructure and investment."
      ],
      quickCheck: [
        {
          question:
            "Why is farmland limited in Japan?",
          options: [
            "Much of the country is mountainous",
            "Japan has no people",
            "Japan has no coastline",
            "All land is desert"
          ],
          answer: "Much of the country is mountainous",
          explanation:
            "Mountainous relief limits flat land for farming and settlement."
        },
        {
          question:
            "Which industry is Japan strongly associated with?",
          options: [
            "High-tech manufacturing",
            "Only subsistence farming",
            "Only oil extraction",
            "Only nomadic herding"
          ],
          answer: "High-tech manufacturing",
          explanation:
            "Japan is known for vehicles, electronics, robotics and precision manufacturing."
        }
      ],
      examPractice: [
        {
          question:
            "Explain how Japan’s physical geography affects farming.",
          marks: 6,
          indicativeContent:
            "Japan’s mountainous relief limits the amount of flat farmland. Farming is concentrated on coastal plains, valleys and terraced slopes. Because land is limited, farming is often intensive, using technology, irrigation and careful land management to increase yields. Rice is an important crop in suitable lowland areas."
        },
        {
          question:
            "Explain why Japan has developed high-value manufacturing industries.",
          marks: 6,
          indicativeContent:
            "Japan has limited raw materials, so it imports many resources and adds value through manufacturing. Skilled workers, education, technology, research, transport infrastructure and investment support industries such as vehicles, electronics, robotics and precision engineering. Exporting high-value products helps Japan earn income."
        }
      ],
      grade9Move:
        "Use ‘limited raw materials but high value-added manufacturing’ as the core explanation for Japan’s industrial success."
    },

    {
      id: "geo10-8-japan-osaka",
      title: "Japan — Osaka",
      slug: "japan-osaka",
      pageRef: "Source booklet p.139",
      learningObjectives: [
        "Locate Osaka within Japan.",
        "Describe Osaka’s importance as an urban and industrial centre.",
        "Compare Osaka’s opportunities and challenges with Lagos."
      ],
      keywords: [
        {
          term: "Osaka",
          definition:
            "A major city in Japan and an important industrial, commercial and transport centre."
        },
        {
          term: "Urban area",
          definition:
            "A built-up area such as a town or city."
        },
        {
          term: "Port",
          definition:
            "A place on the coast where ships load and unload goods."
        },
        {
          term: "Industrial centre",
          definition:
            "A place with a concentration of factories, businesses and economic activity."
        },
        {
          term: "Infrastructure",
          definition:
            "Basic systems such as transport, water, energy, communications and public services."
        },
        {
          term: "Urban planning",
          definition:
            "The process of designing and managing the development of towns and cities."
        },
        {
          term: "Congestion",
          definition:
            "Overcrowding on roads or transport networks."
        },
        {
          term: "Earthquake-resistant design",
          definition:
            "Building design that reduces damage and risk during earthquakes."
        }
      ],
      tutorial: [
        {
          heading: "Osaka’s location",
          body:
            "Osaka is located on Honshu, Japan’s largest island. It is part of a highly urbanised and industrialised region and has strong transport links, including port, rail and road connections."
        },
        {
          heading: "Economic importance",
          body:
            "Osaka is an important centre for manufacturing, commerce, technology, services and transport. Its location helps connect Japan to domestic and international markets."
        },
        {
          heading: "Urban opportunities",
          body:
            "Osaka provides jobs, education, healthcare, public transport, cultural activities and business opportunities. Strong infrastructure supports a high standard of living."
        },
        {
          heading: "Urban challenges",
          body:
            "Osaka faces challenges such as congestion, high land values, ageing population, pollution pressure and exposure to natural hazards, especially earthquakes."
        }
      ],
      workedExamples: [
        {
          question:
            "Explain why Osaka is important to Japan’s economy.",
          answer:
            "Osaka is a major urban and industrial centre with strong transport links, businesses, manufacturing, services and trade. Its port and infrastructure help connect Japan to markets."
        },
        {
          question:
            "Explain one challenge facing Osaka.",
          answer:
            "Osaka faces earthquake risk because Japan lies near tectonic plate boundaries. Buildings and infrastructure need strong hazard-resistant design, which can be expensive."
        }
      ],
      commonMisconceptions: [
        "Osaka is not just a residential city; it is an economic and industrial centre.",
        "Wealthier cities still face problems such as congestion, high costs and hazard risk.",
        "Urban challenges in Osaka differ from Lagos because the level of infrastructure and development is different."
      ],
      quickCheck: [
        {
          question:
            "Which country is Osaka in?",
          options: ["Japan", "Nigeria", "Brazil", "India"],
          answer: "Japan",
          explanation:
            "Osaka is one of Japan’s major cities."
        },
        {
          question:
            "Which hazard is especially relevant to Osaka and Japan?",
          options: ["Earthquakes", "Tropical rainforest fires only", "Glacial erosion", "Desertification only"],
          answer: "Earthquakes",
          explanation:
            "Japan lies near tectonic plate boundaries and experiences earthquake risk."
        }
      ],
      examPractice: [
        {
          question:
            "Explain why Osaka is an important urban area.",
          marks: 6,
          indicativeContent:
            "Osaka is important because it is a major city and economic centre in Japan. It has manufacturing, services, commerce, technology and strong transport links. Its port and rail connections support trade and movement of goods and people. It provides jobs, education, healthcare and cultural opportunities."
        },
        {
          question:
            "Compare one opportunity and one challenge in Osaka.",
          marks: 4,
          indicativeContent:
            "One opportunity is access to jobs and services because Osaka has strong infrastructure and many businesses. One challenge is natural hazard risk because Japan is earthquake-prone, so buildings and infrastructure must be designed to reduce damage."
        }
      ],
      grade9Move:
        "When comparing Osaka and Lagos, avoid saying one is simply ‘better’. Compare the type and scale of challenges: infrastructure pressure in Lagos versus ageing, cost and hazard planning in Osaka."
    },

    {
      id: "geo10-9-comparison-nigeria-and-japan",
      title: "Comparison of Nigeria and Japan",
      slug: "comparison-nigeria-and-japan",
      pageRef: "Source booklet p.140",
      learningObjectives: [
        "Compare the physical geography of Nigeria and Japan.",
        "Compare population, farming, industry and urbanisation.",
        "Explain how contrasting regions face different opportunities and challenges."
      ],
      keywords: [
        {
          term: "Comparison",
          definition:
            "Identifying similarities and differences between places, processes or patterns."
        },
        {
          term: "Contrast",
          definition:
            "A clear difference between two places or things."
        },
        {
          term: "Development gap",
          definition:
            "The difference in development between richer and poorer places."
        },
        {
          term: "Urbanisation",
          definition:
            "An increase in the proportion of people living in towns and cities."
        },
        {
          term: "Ageing population",
          definition:
            "A population with a rising proportion of older people."
        },
        {
          term: "Youthful population",
          definition:
            "A population with a high proportion of children and young people."
        },
        {
          term: "Industrial structure",
          definition:
            "The pattern of economic activity across different sectors."
        },
        {
          term: "Physical constraint",
          definition:
            "A natural factor that limits human activity, such as mountains, drought or hazard risk."
        }
      ],
      tutorial: [
        {
          heading: "Physical contrasts",
          body:
            "Nigeria is a large West African country with varied climates, rivers, savanna, rainforest, coastal areas and the Niger Delta. Japan is an East Asian archipelago with mountainous terrain, limited flat land, long coastlines and significant tectonic hazard risk."
        },
        {
          heading: "Population contrasts",
          body:
            "Nigeria has a large, youthful and rapidly growing population. Japan has a high life expectancy, low fertility rate and ageing population. Both countries have dense urban areas, but the reasons and challenges differ."
        },
        {
          heading: "Economic contrasts",
          body:
            "Nigeria’s economy includes agriculture, oil, manufacturing and growing services, but development is uneven and infrastructure challenges remain. Japan is highly industrialised, with high-tech manufacturing, services and strong infrastructure, but limited raw materials and an ageing workforce."
        },
        {
          heading: "City contrasts",
          body:
            "Lagos is a rapidly growing megacity with major economic opportunities but severe infrastructure pressure. Osaka is a highly developed urban and industrial centre with strong infrastructure, but faces challenges such as ageing, congestion, high land costs and earthquake risk."
        }
      ],
      workedExamples: [
        {
          question:
            "Compare the population challenges facing Nigeria and Japan.",
          answer:
            "Nigeria’s challenge is rapid population growth and a youthful population, creating pressure for schools, jobs, housing and services. Japan’s challenge is an ageing and possibly declining population, creating pressure on pensions, healthcare and the workforce."
        },
        {
          question:
            "Compare one physical challenge in Nigeria and Japan.",
          answer:
            "Nigeria faces climate and environmental challenges such as dry northern areas and oil pollution in the Niger Delta. Japan faces tectonic hazards such as earthquakes, volcanoes and tsunamis because it lies near plate boundaries."
        }
      ],
      commonMisconceptions: [
        "Comparison answers must include both countries, not just one.",
        "Nigeria and Japan both have opportunities and challenges, but the type of challenge differs.",
        "Higher development does not mean no problems; Japan still faces ageing, resource dependence and hazards."
      ],
      quickCheck: [
        {
          question:
            "Which country has the stronger ageing population issue?",
          options: ["Japan", "Nigeria", "Both exactly the same", "Neither"],
          answer: "Japan",
          explanation:
            "Japan has high life expectancy and low fertility, creating an ageing population."
        },
        {
          question:
            "Which city is linked to Nigeria?",
          options: ["Lagos", "Osaka", "Tokyo", "Kyoto"],
          answer: "Lagos",
          explanation:
            "Lagos is Nigeria’s largest city and major economic centre."
        },
        {
          question:
            "Which city is linked to Japan?",
          options: ["Osaka", "Lagos", "Abuja", "Kano"],
          answer: "Osaka",
          explanation:
            "Osaka is a major city in Japan."
        }
      ],
      examPractice: [
        {
          question:
            "Compare the population challenges facing Nigeria and Japan.",
          marks: 6,
          indicativeContent:
            "Nigeria has a youthful and rapidly growing population, so it must provide enough schools, jobs, housing, healthcare and infrastructure. This can be an opportunity if young people are educated and employed. Japan has an ageing population caused by high life expectancy and low fertility, creating pressure on pensions, healthcare and the workforce. Both face population challenges, but Nigeria’s are linked to rapid growth while Japan’s are linked to ageing and decline."
        },
        {
          question:
            "Compare economic activity in Nigeria and Japan.",
          marks: 8,
          indicativeContent:
            "Nigeria’s economy includes farming, oil, manufacturing and growing services. Oil exports are important, but reliance on oil creates risks from price changes and pollution. Farming remains important, especially in rural areas. Japan has a more developed industrial economy based on high-value manufacturing, technology, vehicles, electronics and services. Japan imports many raw materials and exports manufactured goods. Nigeria is developing rapidly but faces infrastructure and inequality challenges, while Japan is wealthy but faces resource dependence and an ageing workforce."
        },
        {
          question:
            "Assess how physical geography affects development in Nigeria and Japan.",
          marks: 8,
          indicativeContent:
            "Nigeria’s physical geography affects development through climate variation, rivers, fertile areas, savanna, rainforest and the Niger Delta. Oil in the Niger Delta supports income but causes environmental risks. Drier northern areas can limit farming and water supply. Japan’s mountainous relief limits flat land, concentrating people and industry on coastal plains. Tectonic hazards require expensive planning and earthquake-resistant infrastructure, but Japan’s coastline supports ports and trade. Overall, both countries are shaped by physical geography, but in different ways."
        }
      ],
      grade9Move:
        "Use paired comparison sentences: ‘Whereas Nigeria…, Japan…’. This keeps both countries in the answer and makes comparison explicit."
    }
  ],

  sectionReview: {
    title: "Section Ten Review — A Study of Two Contrasting Regions",
    summary:
      "Nigeria and Japan are contrasting regions with different physical landscapes, populations, economies and urban challenges. Nigeria is a large West African country with rapid population growth, a youthful population, oil resources, farming and fast-growing cities such as Lagos. Japan is a highly developed East Asian archipelago with mountainous land, tectonic hazards, an ageing population, high-tech industry and major cities such as Osaka. Comparing the two helps show how physical and human geography interact to shape development.",
    mustKnow: [
      "Nigeria is in West Africa and has varied physical geography, including savanna, rainforest, rivers, coast and the Niger Delta.",
      "Nigeria has a large, youthful and rapidly growing population.",
      "Nigeria’s economy includes farming, oil, manufacturing and services.",
      "The Niger Delta is important for oil but faces environmental problems.",
      "Lagos is Nigeria’s largest city and a major economic centre.",
      "Lagos has grown because of rural-urban migration and natural increase.",
      "Lagos faces challenges such as informal settlements, congestion, pollution and pressure on services.",
      "Japan is an island country in East Asia and is an archipelago.",
      "Japan is mountainous, so flat land for farming and settlement is limited.",
      "Japan is affected by tectonic hazards such as earthquakes, volcanoes and tsunamis.",
      "Japan has an ageing population due to high life expectancy and low fertility.",
      "Japan has high-value industries such as electronics, vehicles, robotics and precision manufacturing.",
      "Osaka is a major Japanese industrial, commercial and transport centre.",
      "Nigeria’s population challenge is rapid growth; Japan’s is ageing and possible decline.",
      "Nigeria relies more on natural resources and developing industry; Japan relies more on high-value manufacturing and services.",
      "Both countries show how physical geography influences settlement, farming, industry and risk."
    ],
    highValueExamPhrases: [
      "Nigeria and Japan are contrasting regions because...",
      "Whereas Nigeria has a youthful and rapidly growing population, Japan has an ageing population...",
      "Nigeria’s physical geography creates both resource opportunities and environmental challenges...",
      "Japan’s mountainous relief concentrates settlement and industry on coastal plains...",
      "Lagos demonstrates rapid urban growth and infrastructure pressure...",
      "Osaka demonstrates high levels of development but continued urban and hazard challenges...",
      "The contrast shows that development creates different types of opportunity and risk...",
      "Physical geography influences economic activity, but human factors such as technology and investment also matter..."
    ],
    mixedPractice: [
      {
        question:
          "Compare the physical geography of Nigeria and Japan.",
        marks: 6,
        indicativeContent:
          "Nigeria is a large West African country with rivers, savanna, rainforest, coastal lowlands and the Niger Delta. Its climate varies from wetter south to drier north. Japan is an East Asian archipelago with mountainous relief, limited flat land, long coastlines and tectonic hazards such as earthquakes and volcanoes. Both have coastlines, but Japan is more strongly affected by tectonic risk."
      },
      {
        question:
          "Explain why Lagos has grown rapidly.",
        marks: 6,
        indicativeContent:
          "Lagos has grown rapidly because of rural-urban migration and natural increase. People move to Lagos for jobs, education, healthcare and services. Its coastal location, port, markets and industries attract businesses and workers. However, growth has created pressure on housing, transport, water and sanitation."
      },
      {
        question:
          "Explain why Japan has an ageing population.",
        marks: 6,
        indicativeContent:
          "Japan has an ageing population because life expectancy is high and fertility rates are low. Good healthcare, diet and living standards mean people live longer. Fewer babies are being born, so the proportion of young people falls while the proportion of older people rises."
      },
      {
        question:
          "Compare farming and industry in Nigeria and Japan.",
        marks: 8,
        indicativeContent:
          "Nigeria has important farming, with crops varying by climate from wetter southern regions to drier northern areas. It also has oil, especially in the Niger Delta, plus growing manufacturing and services. Japan has limited farmland because it is mountainous, so farming is often intensive and focused on limited flat land. Japan has high-value manufacturing, vehicles, electronics, robotics and services. Nigeria has important natural resources, while Japan imports many raw materials and adds value through technology."
      },
      {
        question:
          "Assess the challenges facing Lagos and Osaka.",
        marks: 8,
        indicativeContent:
          "Lagos faces rapid urban growth, informal settlements, congestion, pollution, waste issues and pressure on water, sanitation and housing. These challenges are linked to rapid population growth and infrastructure struggling to keep up. Osaka has stronger infrastructure and higher development, but faces congestion, high land values, ageing population and earthquake risk. Both cities face urban challenges, but Lagos is more affected by rapid growth and service pressure, while Osaka is more affected by ageing, cost and hazard planning."
      }
    ]
  }
};

export default geographyContent10;